import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import { CloudAccount } from "@lib/ShiOneClient";
import _ from "lodash";
import {
  assetItemsToShowType,
  cloudAwsItemsToShowType,
  cloudAzureItemsToShowType,
  cloudDatadogItemsToShowType,
  cloudGcpItemsToShowType,
  cloudOffice365ItemsToShowType,
  hasPermissionType,
  hasRoleInType,
  homeItemsToShowType,
  servicesItemsToShowType,
  settingsItemsToShowType,
  supportItemsToShowType,
} from "./NavItemsToShowTypes";

export const homeItemsToShow = (
  hasPermission: hasPermissionType,
  hasCloudReports: boolean,
  isAdmin: boolean,
): homeItemsToShowType => {
  return {
    showMonitoring:
      hasPermission(ShiOnePermission.azureMonitoringAlertRead) ||
      hasPermission(ShiOnePermission.awsMonitoringAlertRead),
    showCloudConsumption:
      hasCloudReports &&
      (isAdmin ||
        hasPermission(ShiOnePermission.multicloudConsumptionRead)),
  };
};

export const supportItemsToShow = (
  hasPermission: hasPermissionType,
  hasRoleIn: hasRoleInType,
  hasDevices: boolean,
): supportItemsToShowType => {
  return {
    showServiceReport: hasRoleIn("CustomerAdmin"),
    showMyDevices: !hasRoleIn("ShiImpersonateVirtualAdmin") && hasDevices,
    showKnowledgeBase: hasPermission(ShiOnePermission.knowledgeBaseRead),
  };
};

export const assetItemsToShow = (
  hasPermission: hasPermissionType,
  hasDattoRmmIntegration: boolean,
  hasCiscoIntegrations: boolean,
): assetItemsToShowType => {
  return {
    showDiscoveredAssets: hasPermission(ShiOnePermission.assetEstateRead),
    showManagedDevices:
      hasDattoRmmIntegration &&
      hasPermission(ShiOnePermission.managedDevicesRead),
    showWarehouseStock: hasPermission(ShiOnePermission.warehouseInventoryRead),
    showWarehouseOrders: hasPermission(ShiOnePermission.warehouseOrderRead),
    showCiscoInstallBaseAnalysis:
      hasCiscoIntegrations && hasPermission(ShiOnePermission.ciscoReportRead),
    showPurchasedAssets:
      hasPermission(ShiOnePermission.assetOrderDataRead) ||
      hasPermission(ShiOnePermission.assetOrderDataManage),
    showAssetLifecycleManagement:
      hasPermission(ShiOnePermission.assetOrderDataRead) ||
      hasPermission(ShiOnePermission.assetOrderDataManage) ||
      hasPermission(ShiOnePermission.renewalsView),
    showAssetReports: hasPermission(ShiOnePermission.assetEstateRead),
  };
};

export const cloudAwsItemsToShow = (
  hasPermission: hasPermissionType,
  cloudContracts: CloudAccount[],
  isAdmin: boolean,
  isGuestUser: boolean,
): cloudAwsItemsToShowType => {
  let hasAwsMasterPayerAccounts = false;

  if (cloudContracts && cloudContracts.length > 0) {
    cloudContracts.forEach((cc) => {
      if (
        cc.cloud === "AWS" &&
        cc.userDefinedFieldsJson &&
        cc.userDefinedFieldsJson !== "[]"
      ) {
        const awsPayerID = _.find(
          JSON.parse(cc.userDefinedFieldsJson),
          (udf) => {
            return udf.Name === "AWS Payer ID";
          },
        );
        if (awsPayerID && awsPayerID.Value) {
          hasAwsMasterPayerAccounts = true;
        }
      }
    });
  }
  return {
    showAccounts:
      hasPermission(ShiOnePermission.awsAccountRead),
    showConsumption: hasPermission(ShiOnePermission.awsConsumptionRead),
    showBlueprints: hasPermission(ShiOnePermission.awsBlueprintDeploy),
    showCostAllocationTags: isAdmin && hasAwsMasterPayerAccounts,
    showInvoices: hasPermission(ShiOnePermission.serviceInvoiceRead),
    showMonitoring: hasPermission(ShiOnePermission.awsMonitoringAlertRead),
    showRecommendations: hasPermission(ShiOnePermission.awsRecommendationRead),
    showGovernance: hasPermission(ShiOnePermission.awsComplianceRead),
  };
};

export const cloudAzureItemsToShow = ({
  hasPermission,
  hasAzureBackupIntegrations,
  hasAzureComplianceIntegrations,
  hasAzureGovernanceReportsIntegrations,
}: {
  hasPermission: hasPermissionType;
  hasAzureBackupIntegrations: boolean;
  hasAzureComplianceIntegrations: boolean;
  hasAzureGovernanceReportsIntegrations: boolean;
}): cloudAzureItemsToShowType => {
  return {
    showBackup:
      hasPermission(ShiOnePermission.azureBackupAlertRead) &&
      hasAzureBackupIntegrations,
    showBlueprints: hasPermission(ShiOnePermission.azureBlueprintDeploy),
    showInvoices: hasPermission(ShiOnePermission.serviceInvoiceRead),
    showMonitoring: hasPermission(ShiOnePermission.azureMonitoringAlertRead),
    showRecommendations: hasPermission(
      ShiOnePermission.azureRecommendationRead,
    ),
    showAllUpRecommendations: hasPermission(
      ShiOnePermission.azureRecommendationRead,
    ),
    showReports: hasPermission(ShiOnePermission.azureConsumptionRead),
    showSecurityAndCompliance:
      hasPermission(ShiOnePermission.azureComplianceRead) &&
      hasAzureComplianceIntegrations,
    showSubscriptions: hasPermission(ShiOnePermission.azureAccountRead),
    showGovernance:
      hasPermission(ShiOnePermission.azureComplianceRead) &&
      hasAzureGovernanceReportsIntegrations,
  };
};

export const cloudGcpItemsToShow = ({
  hasPermission,
}: {
  hasPermission: hasPermissionType;
}): cloudGcpItemsToShowType => {
  return {
    showInvoices: hasPermission(ShiOnePermission.serviceInvoiceRead),
    showProjects: hasPermission(ShiOnePermission.gcpAccountRead),
    showReports: hasPermission(ShiOnePermission.gcpConsumptionRead),
  };
};

export const cloudOffice365ItemsToShow = (
  hasPermission: hasPermissionType,
): cloudOffice365ItemsToShowType => {
  return {
    showInvoices: hasPermission(ShiOnePermission.serviceInvoiceRead),
    showSubscriptions: hasPermission(ShiOnePermission.o365AccountRead),
  };
};

export const cloudDatadogItemsToShow = (
  hasPermission: hasPermissionType,
  isAdmin: boolean,
  hasDatadogUsageIntegration: boolean,
  contosoDemo: boolean,
): cloudDatadogItemsToShowType => {
  return {
    showReports:
      ((isAdmin ||
        hasPermission(ShiOnePermission.datadogConsumptionRead)) &&
        hasDatadogUsageIntegration) ||
      (isAdmin && contosoDemo),
  };
};

export const servicesItemsToShow = (
  hasPermission: hasPermissionType,
  isAdmin: boolean,
  fsaID: number | undefined,
  hasCatalyst: boolean,
): servicesItemsToShowType => {
  return {
    showContracts:
      isAdmin || hasPermission(ShiOnePermission.serviceContractRead),
    showAllProjects:
      isAdmin || hasPermission(ShiOnePermission.serviceProjectRead),
    showInvoices: isAdmin || hasPermission(ShiOnePermission.serviceInvoiceRead),
    showFlexibleServiceAgreement: isAdmin && !!fsaID,
    showCatalyst: hasCatalyst,
  };
};

export const settingsItemsToShow = (
  isAdmin: boolean,
  myManagedSecurityGroups: any[],
): settingsItemsToShowType => {
  return {
    showGeneral: isAdmin,
    showIntegrations: isAdmin,
    showUserManagement: isAdmin,
    showSecurityGroups: isAdmin || myManagedSecurityGroups.length > 0,
  };
};
